import React, { useState, useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Grid } from '@mui/material'
import ModalComponent from '../../../components/modals'
import SimpleSelect from '../../../components/selects/simpleSelect'
import FormAutocomplete from '../../../components/selects/formAutocomplete'
import FormTextField from '../../../components/textField/formTextField'
import FormCalendar from '../../../components/calendar/formCalendar'
import LaddaButton from '../../../components/buttons/laddaButton'
import CustomSnackbar from '../../../components/snackbar'
import {
  callApiAsync,
  getCountries,
  getTransportModes,
  checkExternalSurveyByOrderNumber,
  createExternalOrderInformation,
  generateExternalSurveyLink
} from '../../../services/apiService'
import { icons } from '../../../constants'
import { format } from 'date-fns'
import { getTodayDateTime } from '../../../utils/dateUtils'

const validationSchema = yup.object().shape({
  rows: yup.array().of(
    yup.object().shape({
      destino: yup.mixed().required('El destino es obligatorio').nullable(),
      numeroOrden: yup.string().trim().required('El Nº de orden es obligatorio'),
      producto: yup.string().trim().required('El producto es obligatorio'),
      modalidadTransporte: yup.mixed().required('La modalidad de transporte es obligatoria').nullable(),
      fechaSalidaOrigen: yup.mixed().required('La fecha de salida de origen es obligatoria').nullable(),
      fechaLlegadaAduanas: yup.mixed().required('La fecha de llegada a aduanas es obligatoria').nullable(),
      fechaLlegadaCliente: yup.mixed().nullable()
    })
  ).min(1)
})

const defaultRow = () => ({
  destino: null,
  numeroOrden: '',
  producto: '',
  modalidadTransporte: null,
  fechaSalidaOrigen: null,
  fechaLlegadaAduanas: null,
  fechaLlegadaCliente: null
})

const GenerateSurveyLinkModal = ({ open, handleClose }) => {
  const [loading, setLoading] = useState(false)
  const [countryOptions, setCountryOptions] = useState([{ name: 'Seleccionar país', value: null }])
  const [transportModeOptions, setTransportModeOptions] = useState([{ name: 'Seleccionar modalidad', value: null }])
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState('success')
  const [openSuccessModal, setOpenSuccessModal] = useState(false)
  const [openAlreadyExistsModal, setOpenAlreadyExistsModal] = useState(false)
  const [generatedLink, setGeneratedLink] = useState('')

  const {
    control,
    handleSubmit,
    watch,
    reset,
    getValues,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
    defaultValues: {
      rows: [defaultRow()]
    }
  })

  const { fields } = useFieldArray({
    control,
    name: 'rows'
  })

  const watchFechaSalidaOrigen = watch('rows.0.fechaSalidaOrigen')
  const watchFechaLlegadaAduanas = watch('rows.0.fechaLlegadaAduanas')

  useEffect(() => {
    if (open) {
      loadOptions()
      reset({ rows: [defaultRow()] })
    }
  }, [open])

  const loadOptions = async () => {
    await callApiAsync(
      () => getCountries(),
      (resp) => {
        const data = resp?.data ?? resp
        const countries = Array.isArray(data) ? data : []
        setCountryOptions([
          { name: 'Seleccionar país', value: null },
          ...countries.map((c) => ({ name: c.name, value: c.id }))
        ])
      },
      null
    )
    await callApiAsync(
      () => getTransportModes(),
      (resp) => {
        const data = resp?.data ?? resp
        const modes = Array.isArray(data) ? data : []
        setTransportModeOptions([
          { name: 'Seleccionar modalidad', value: null },
          ...modes.map((m) => ({ name: m.name, value: m.id }))
        ])
      },
      null
    )
  }

  const buildFullUrl = (surveyUrl, surveyLinkToken) => {
    if (surveyUrl?.startsWith('http')) return surveyUrl
    return `${window.location.origin}${surveyUrl || `/foreign_trade_board/external_client_satisfaction/international/answer/${surveyLinkToken}`}`
  }

  const onSubmit = async () => {
    const { rows } = getValues()
    const row = rows[0]
    const orderNumber = (row?.numeroOrden ?? '').toString().trim()
    setLoading(true)
    try {
      const checkResp = await checkExternalSurveyByOrderNumber(orderNumber)
      const checkData = checkResp?.data ?? checkResp
      if (checkData?.exists && (checkData.surveyUrl || checkData.surveyLinkToken)) {
        setGeneratedLink(buildFullUrl(checkData.surveyUrl, checkData.surveyLinkToken))
        setOpenAlreadyExistsModal(true)
        setLoading(false)
        return
      }
      const destinationCountryId = row.destino?.value ?? row.destino
      const product = (row.producto ?? '').toString().trim()
      const transportModeId = row.modalidadTransporte?.value ?? row.modalidadTransporte
      const departureDateOrigin = row.fechaSalidaOrigen ? format(new Date(row.fechaSalidaOrigen), 'yyyy-MM-dd') : null
      const arrivalDateCustoms = row.fechaLlegadaAduanas ? format(new Date(row.fechaLlegadaAduanas), 'yyyy-MM-dd') : null
      const arrivalDateClientFacilities = row.fechaLlegadaCliente ? format(new Date(row.fechaLlegadaCliente), 'yyyy-MM-dd') : null
      const orderPayload = {
        order_number: orderNumber,
        destination_country_id: destinationCountryId,
        product,
        transport_mode_id: transportModeId,
        departure_date_origin: departureDateOrigin,
        arrival_date_customs: arrivalDateCustoms,
        arrival_date_client_facilities: arrivalDateClientFacilities || null
      }
      const createResp = await createExternalOrderInformation(orderPayload)
      const data = createResp?.data ?? createResp
      const orderInfoId = data?.id
      if (!orderInfoId) {
        setSnackbarMessage(data?.error || 'No se pudo guardar la información de la orden')
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
        setLoading(false)
        return
      }
      const surveyResp = await generateExternalSurveyLink({
        orderInformationId: orderInfoId,
        creation_datetime: getTodayDateTime()
      })
      const surveyData = surveyResp?.data ?? surveyResp
      setGeneratedLink(buildFullUrl(surveyData?.surveyUrl, surveyData?.surveyOrderId || surveyData?.id))
      setOpenSuccessModal(true)
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.detail || e?.message || 'Error al verificar o generar enlace'
      setSnackbarMessage(msg)
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    }
    setLoading(false)
  }

  const handleCopyLink = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink).then(() => {
        setSnackbarMessage('Link copiado al portapapeles')
        setSnackbarSeverity('success')
        setSnackbarOpen(true)
      }).catch(() => {
        setSnackbarMessage('Error al copiar el link')
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
      })
    }
  }

  const handleCloseSuccessModal = () => {
    setOpenSuccessModal(false)
    setGeneratedLink('')
    handleClose()
    reset({ rows: [defaultRow()] })
  }

  const handleCloseAlreadyExistsModal = () => {
    setOpenAlreadyExistsModal(false)
    setGeneratedLink('')
    handleClose()
    reset({ rows: [defaultRow()] })
  }

  const handleCloseModal = () => {
    reset({ rows: [defaultRow()] })
    handleClose()
  }

  return (
    <div>
      <ModalComponent
        open={open}
        handleClose={handleCloseModal}
        title="Generar Enlace para Orden de Importación"
        width="960px"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          {fields.map((field, index) => (
            <Grid container item xs={12} spacing={2} key={field.id}>
              <Grid item xs={12} md={6}>
                <p className="create-label ml-0">Destino</p>
                <FormAutocomplete
                  name={`rows.${index}.destino`}
                  control={control}
                  options={countryOptions}
                  formLabel=""
                  label=""
                  margin="0"
                  error={errors?.rows?.[index]?.destino}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <p className="create-label ml-0">Nº orden</p>
                <FormTextField
                  name={`rows.${index}.numeroOrden`}
                  control={control}
                  formLabel=""
                  label=""
                  margin="0"
                  error={errors?.rows?.[index]?.numeroOrden}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <p className="create-label ml-0">Producto</p>
                <FormTextField
                  name={`rows.${index}.producto`}
                  control={control}
                  formLabel=""
                  label=""
                  margin="0"
                  error={errors?.rows?.[index]?.producto}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <p className="create-label ml-0">Modalidad de transporte</p>
                <SimpleSelect
                  name={`rows.${index}.modalidadTransporte`}
                  control={control}
                  options={transportModeOptions}
                  margin="0"
                  hideLabel
                  error={errors?.rows?.[index]?.modalidadTransporte}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <p className="create-label ml-0">Fecha salida de origen</p>
                <FormCalendar
                  name={`rows.${index}.fechaSalidaOrigen`}
                  control={control}
                  formLabel=""
                  margin="0"
                  error={errors?.rows?.[index]?.fechaSalidaOrigen}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <p className="create-label ml-0">Fecha llegada a aduanas</p>
                <FormCalendar
                  name={`rows.${index}.fechaLlegadaAduanas`}
                  control={control}
                  formLabel=""
                  margin="0"
                  minDate={index === 0 ? watchFechaSalidaOrigen : undefined}
                  error={errors?.rows?.[index]?.fechaLlegadaAduanas}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <p className="create-label ml-0">Fecha llegada instalaciones cliente</p>
                <FormCalendar
                  name={`rows.${index}.fechaLlegadaCliente`}
                  control={control}
                  formLabel=""
                  margin="0"
                  minDate={index === 0 ? watchFechaLlegadaAduanas : undefined}
                />
              </Grid>
            </Grid>
          ))}
          <Grid item xs={12} className="j-center" style={{ marginTop: 16 }}>
            <LaddaButton type="submit" variant="contained" disabled={loading}>
              <div className="HContainer align-center">
                <img className="btn-icon" src={icons.plusIcon} alt="icon" style={{ marginRight: '8px' }} />
                <p className="button_text">Crear información de orden y enlace</p>
              </div>
            </LaddaButton>
          </Grid>
        </Grid>
        </form>
      </ModalComponent>

      <ModalComponent
        open={openAlreadyExistsModal}
        handleClose={handleCloseAlreadyExistsModal}
        title="Encuesta ya existente"
        width="600px"
        actions={
          <div className="w-complete mv-20">
            <div className="j-center">
              <LaddaButton variant="contained" onClick={handleCloseAlreadyExistsModal} backgroundColor="#6C5CE7">
                <div className="HContainer align-center">
                  <p className="button_text">Aceptar</p>
                </div>
              </LaddaButton>
            </div>
          </div>
        }
      >
        <p className="create-label ml-0">
          Ya existe una encuesta con una URL en el sistema para este Nº de orden.
        </p>
        <div
          className="HContainer a-center"
          style={{
            gap: '10px',
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            marginTop: '15px',
            border: '1px solid #e0e0e0'
          }}
        >
          <img src={icons.linkBlue} alt="Link" style={{ width: '20px', height: '20px' }} />
          <span style={{ fontSize: '14px', color: '#333', wordBreak: 'break-all', flex: 1 }}>
            {generatedLink}
          </span>
          <span
            onClick={handleCopyLink}
            style={{
              color: '#6C5CE7',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              whiteSpace: 'nowrap'
            }}
          >
            Copiar Link
          </span>
        </div>
      </ModalComponent>

      <ModalComponent
        open={openSuccessModal}
        handleClose={handleCloseSuccessModal}
        title=""
        width="600px"
        actions={
          <div className="w-complete mv-20">
            <div className="j-center">
              <LaddaButton variant="contained" onClick={handleCloseSuccessModal} backgroundColor="#6C5CE7">
                <div className="HContainer align-center">
                  <p className="button_text">Aceptar</p>
                </div>
              </LaddaButton>
            </div>
          </div>
        }
      >
        <div>
          <h2
            className="title-bio-details-chard"
            style={{
              marginBottom: '20px',
              textAlign: 'center',
              color: '#6C5CE7',
              fontSize: '20px'
            }}
          >
            Encuesta Satisfacción al Cliente Externo Creada con Éxito
          </h2>
          <p
            className="detail-label"
            style={{
              marginBottom: '15px',
              fontSize: '14px',
              color: '#666',
              textAlign: 'center'
            }}
          >
            Link de acceso para los empleados encuestados
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #e0e0e0'
            }}
          >
            <img src={icons.linkBlue} alt="Link" style={{ width: '20px', height: '20px' }} />
            <span
              style={{
                fontSize: '14px',
                color: '#333',
                wordBreak: 'break-all',
                flex: 1,
                textAlign: 'center'
              }}
            >
              {generatedLink}
            </span>
            <span
              onClick={handleCopyLink}
              style={{
                color: '#6C5CE7',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                whiteSpace: 'nowrap'
              }}
            >
              Copiar Link
            </span>
          </div>
        </div>
      </ModalComponent>

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </div>
  )
}

export default GenerateSurveyLinkModal
